/*
Based on Material Dashboard 2 React by Creative Tim (https://www.creative-tim.com)
Licensed under the MIT License (see LICENSE)

Original Copyright (c) 2013-2021 Creative Tim
Modifications (c) 2025 CodeWeave Solutions (Pty) Ltd
*/

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Papa from "papaparse";
import fdLogo from '../assets/images/logo-fisher-dugmore-financial.png';

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import ListSubheader from "@mui/material/ListSubheader";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Variable groups for dropdown
const VARIABLE_GROUPS = [
  {
    label: 'individuals',
    options: [
      'first name',
      'last name',
      'id number',
      'email',
      'phone',
      'address',
      'tax number',
      'date of birth',
    ],
  },
  {
    label: 'entities',
    options: [
      'entity name',
      'organization legal name',
      'entity registration number',
      'license number',
      'address',
    ],
  },
  {
    label: 'financial advisor',
    options: [
      'first name',
      'last name',
      'full name',
      'id number',
      'email',
      'branch',
    ],
  },
];

function ContractTemplateUploadModal({ isOpen, onClose, onSave }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [previewContent, setPreviewContent] = useState([]);
  const [processedPreview, setProcessedPreview] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [templateName, setTemplateName] = useState("");
  const [fieldGroups, setFieldGroups] = useState({});

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Update preview whenever form values change
  useEffect(() => {
    if (previewContent.length > 0) {
      const updatedPreview = previewContent.map(item => {
        let content = item.text;
        formFields.forEach(field => {
          const variable = formValues[field.id] || field.label.replace(/\s+/g, '_');
          const group = fieldGroups[field.id] || '';
          const placeholder = `{{${field.id}}}`;
          const groupVar = group ? `${group}_${variable.replace(/\s+/g, '_')}` : variable.replace(/\s+/g, '_');
          content = content.replace(new RegExp(placeholder, 'g'), `{{${groupVar}}}`);
        });
        return {
          ...item,
          processedText: content
        };
      });
      setProcessedPreview(updatedPreview);
    }
  }, [formValues, fieldGroups, previewContent, formFields]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (fileExtension === 'csv') {
        setFile(selectedFile);
        setError("");
        setIsProcessing(true);

        Papa.parse(selectedFile, {
          header: true,
          complete: (results) => {
            try {
              // Validate CSV structure
              if (!results.data.length) {
                throw new Error("CSV file is empty");
              }

              const requiredColumns = ['Content', 'Type'];
              const firstRow = results.data[0];
              const missingColumns = requiredColumns.filter(col => !(col in firstRow));
              
              if (missingColumns.length) {
                throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
              }

              // Process fields and content
              const fields = [];
              const content = [];

              results.data.forEach(row => {
                // Extract form fields from content
                const matches = row.Content.match(/{{([^}]+)}}/g);
                if (matches) {
                  matches.forEach(match => {
                    const fieldName = match.slice(2, -2);
                    if (!fields.find(f => f.id === fieldName)) {
                      fields.push({
                        id: fieldName,
                        label: fieldName.split('_').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' '),
                        type: row.Type === 'Multiline Input' ? 'multiline' : 'text',
                        required: true
                      });
                    }
                  });
                }

                // Add to preview content
                content.push({
                  text: row.Content,
                  type: row.Type
                });
              });

              setFormFields(fields);
              setPreviewContent(content);
              const initialValues = {};
              fields.forEach(field => {
                initialValues[field.id] = '';
              });
              setFormValues(initialValues);
            } catch (err) {
              setError(err.message);
              setFile(null);
            } finally {
              setIsProcessing(false);
            }
          },
          error: (error) => {
            setError("Error parsing CSV: " + error.message);
            setFile(null);
            setIsProcessing(false);
          }
        });
      } else {
        setError("Please upload a .csv file");
        setFile(null);
      }
    }
  };

  const handleFieldChange = (fieldId, value) => {
    // Find the group for the selected value
    let selectedGroup = '';
    VARIABLE_GROUPS.forEach(group => {
      if (group.options.includes(value)) {
        selectedGroup = group.label;
      }
    });
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
    setFieldGroups(prev => ({
      ...prev,
      [fieldId]: selectedGroup
    }));
  };

  const handleSave = () => {
    if (!templateName.trim()) {
      setError("Please enter a template name");
      return;
    }
    onSave({
      file,
      formFields,
      formValues,
      templateName: templateName.trim()
    });
  };

  const renderField = (field) => {
    return (
      <FormControl fullWidth required>
        <InputLabel>Select variable</InputLabel>
        <Select
          value={formValues[field.id] || ''}
          label="Select variable"
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
        >
          {VARIABLE_GROUPS.map((group) => [
            <ListSubheader key={group.label}>{group.label}</ListSubheader>,
            group.options.map((option) => (
              <MenuItem key={group.label + '-' + option} value={option}>
                {option}
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>
    );
  };

  const renderPreviewContent = () => {
    return (
      <MDBox>
        <MDBox display="flex" flexDirection="column" alignItems="center" mb={4}>
          <MDBox
            component="img"
            src={fdLogo}
            alt="Fisher Dugmore Financial Logo"
            sx={{
              width: '120px',
              height: 'auto',
              mb: 2
            }}
          />
          <MDTypography variant="h4" textAlign="center" sx={{ textTransform: 'uppercase' }}>
            {templateName}
          </MDTypography>
        </MDBox>
        {processedPreview.map((item, index) => {
          const text = item.processedText;
          switch (item.type) {
            case 'h1':
              return <MDTypography key={index} variant="h1" mt={3} mb={2}>{text}</MDTypography>;
            case 'h2':
              return <MDTypography key={index} variant="h2" mt={3} mb={2}>{text}</MDTypography>;
            case 'h3':
              return <MDTypography key={index} variant="h3" mt={3} mb={2}>{text}</MDTypography>;
            case 'h4':
              return <MDTypography key={index} variant="h4" mt={3} mb={2}>{text}</MDTypography>;
            case 'h5':
              return <MDTypography key={index} variant="h5" mt={3} mb={2}>{text}</MDTypography>;
            case 'h6':
              return <MDTypography key={index} variant="h6" mt={3} mb={2}>{text}</MDTypography>;
            case 'p':
              return <MDTypography key={index} variant="body1" mb={2}>{text}</MDTypography>;
            case 'span':
              return <span key={index} style={{ display: 'inline' }}>{text}</span>;
            case 'strong':
              return <strong key={index}>{text}</strong>;
            case 'b':
              return <b key={index}>{text}</b>;
            case 'em':
              return <em key={index}>{text}</em>;
            case 'i':
              return <i key={index}>{text}</i>;
            case 'u':
              return <u key={index}>{text}</u>;
            case 'small':
              return <small key={index}>{text}</small>;
            case 'mark':
              return <mark key={index}>{text}</mark>;
            case 'del':
              return <del key={index}>{text}</del>;
            case 'ins':
              return <ins key={index}>{text}</ins>;
            case 'sub':
              return <sub key={index}>{text}</sub>;
            case 'sup':
              return <sup key={index}>{text}</sup>;
            case 'abbr': {
              // Support abbr|Tooltip in content
              const [abbrText, abbrTitle] = text.split('|');
              return <abbr key={index} title={abbrTitle ? abbrTitle.trim() : abbrText}>{abbrText}</abbr>;
            }
            case 'blockquote':
              return <blockquote key={index} style={{ margin: '1em 0', paddingLeft: '1em', borderLeft: '3px solid #ccc' }}>{text}</blockquote>;
            case 'code':
              return <code key={index} style={{ background: '#f5f5f5', padding: '2px 4px', borderRadius: '4px' }}>{text}</code>;
            case 'pre':
              return <pre key={index} style={{ background: '#f5f5f5', padding: '1em', borderRadius: '4px', overflow: 'auto' }}>{text}</pre>;
            default:
              return null;
          }
        })}
      </MDBox>
    );
  };

  const renderJsonStructure = () => {
    const structure = {
      templateName: templateName.toUpperCase(),
      content: previewContent.map(item => ({
        Content: item.text,
        Type: item.type
      }))
    };

    return (
      <MDBox>
        <MDBox display="flex" flexDirection="column" alignItems="center" mb={4}>
          <MDBox
            component="img"
            src={fdLogo}
            alt="Fisher Dugmore Financial Logo"
            sx={{
              width: '120px',
              height: 'auto',
              mb: 2
            }}
          />
          <MDTypography variant="h4" textAlign="center" sx={{ textTransform: 'uppercase' }}>
            {templateName}
          </MDTypography>
        </MDBox>
        <pre style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '1rem', 
          borderRadius: '4px',
          overflow: 'auto',
          maxHeight: '500px'
        }}>
          {JSON.stringify(structure, null, 2)}
        </pre>
      </MDBox>
    );
  };

  return (
    <Dialog 
      open={isOpen}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          m: 0,
          p: 0,
          width: '75vw',
          height: '90vh',
          maxWidth: '75vw',
          maxHeight: '90vh',
          borderRadius: 0,
        }
      }}
    >
      <DialogTitle>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6">
            Upload Form Template
          </MDTypography>
          <IconButton onClick={onClose} size="small">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox pt={2}>
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="regular" color="text">
              Template Name
            </MDTypography>
            <MDInput
              fullWidth
              label="Enter template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
              sx={{ mt: 1 }}
            />
          </MDBox>

          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="regular" color="text">
              Upload CSV Template (.csv)
            </MDTypography>
            <MDBox
              component="input"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              sx={{
                width: "100%",
                mt: 1,
                p: 2,
                border: "1px dashed",
                borderColor: "grey.400",
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.main",
                },
              }}
            />
            {file && (
              <MDTypography variant="caption" color="text" mt={1}>
                Selected file: {file.name}
              </MDTypography>
            )}
            {isProcessing && (
              <MDTypography variant="caption" color="text" mt={1}>
                Processing template...
              </MDTypography>
            )}
          </MDBox>

          {error && (
            <MDTypography variant="caption" color="error" mb={2}>
              {error}
            </MDTypography>
          )}

          {formFields.length > 0 && (
            <MDBox display="flex" height="70vh" gap={4}>
              {/* Form Fields (Dropdowns) */}
              <MDBox flex={1} overflow="auto" pr={2}>
                <MDTypography variant="h6" mb={2}>
                  Form Fields
                </MDTypography>
                {formFields.map((field) => (
                  <MDBox key={field.id} mb={3}>
                    {renderField(field)}
                  </MDBox>
                ))}
              </MDBox>
              {/* Preview */}
              <MDBox flex={2} overflow="auto" p={2} border="1px solid" borderColor="grey.300" borderRadius={2} bgcolor="#fafafa">
                {renderPreviewContent()}
              </MDBox>
            </MDBox>
          )}

          <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <MDButton variant="outlined" color="dark" onClick={onClose}>
              Cancel
            </MDButton>
            <MDButton 
              variant="gradient" 
              color="info" 
              onClick={handleSave}
              disabled={!file || formFields.length === 0 || !templateName.trim()}
            >
              Save
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

ContractTemplateUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default ContractTemplateUploadModal; 